"use client";

import React from "react";
import { Leaf, Droplets, Wind, BrickWall, Layers, Hammer, Construction, Building2, TrainFront, FileText, Download, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const categories = [{
  id: "environment",
  title: "Environmental Engineering",
  description: "Sustainable solutions for a greener future in construction.",
  icon: <Leaf className="size-5" />,
  services: [{
    title: "Advanced Water Treatment",
    desc: "Next-gen filtration systems for industrial construction sites.",
    icon: <Droplets className="size-6 text-ring" />,
    pdfLink: "#"
  }, {
    title: "Solid Waste Management",
    desc: "Circular economy protocols for construction debris recycling.",
    icon: <Layers className="size-6 text-ring" />,
    pdfLink: "#"
  }, {
    title: "Air Quality Control",
    desc: "IoT-based monitoring systems for urban development projects.",
    icon: <Wind className="size-6 text-ring" />,
    pdfLink: "#"
  }]
}, {
  id: "materials",
  title: "Advanced Materials",
  description: "Pioneering research in high-performance construction materials.",
  icon: <BrickWall className="size-5" />,
  services: [{
    title: "Sustainable Concrete",
    desc: "Low-carbon concrete formulations with enhanced durability.",
    icon: <BrickWall className="size-6 text-ring" />,
    pdfLink: "#"
  }, {
    title: "Smart Insulation",
    desc: "Adaptive thermal materials for energy-efficient buildings.",
    icon: <Layers className="size-6 text-ring" />,
    pdfLink: "#"
  }, {
    title: "Self-Healing Materials",
    desc: "Bio-concrete technologies that repair micro-cracks autonomously.",
    icon: <Hammer className="size-6 text-ring" />,
    pdfLink: "#"
  }]
}, {
  id: "infrastructure",
  title: "Infrastructure & Transport",
  description: "Digital transformation for critical infrastructure networks.",
  icon: <Construction className="size-5" />,
  services: [{
    title: "Structural Health Monitoring",
    desc: "Real-time sensor networks for bridge and tunnel safety.",
    icon: <Construction className="size-6 text-ring" />,
    pdfLink: "#"
  }, {
    title: "Underground Engineering",
    desc: "Advanced geotechnical solutions for metro and basement works.",
    icon: <TrainFront className="size-6 text-ring" />,
    pdfLink: "#"
  }, {
    title: "Smart City Integration",
    desc: "BIM-integrated planning for connected urban infrastructure.",
    icon: <Building2 className="size-6 text-ring" />,
    pdfLink: "#"
  }]
}];
export default function ScienceAndTechnology() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="khoa-học-công-nghệ" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-ring/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 tracking-tight">
            Science & Technology
          </h2>
          <p className="text-muted-foreground text-lg">
            Explore our cutting-edge research and technical solutions developed in collaboration with the National University of Civil Engineering.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-4" defaultValue="materials">
            {categories.map((category, index) => <AccordionItem key={category.id} value={category.id} className="border border-border bg-card rounded-lg px-2 shadow-sm data-[state=open]:border-ring/50 data-[state=open]:shadow-md transition-all duration-300" data-index={index}>
                <AccordionTrigger className="px-4 hover:no-underline group">
                  <div className="flex items-center gap-4 text-left">
                    <div className="bg-primary/10 p-2 rounded-md text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      {category.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                        {category.title}
                      </h3>
                      <p className="text-sm text-muted-foreground font-normal hidden md:block">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                
                <AccordionContent className="px-4 pb-6 pt-2">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                    {category.services.map((service, idx) => <div key={idx} className="bg-background border border-border/50 rounded-lg p-5 hover:border-ring/30 hover:shadow-sm transition-all duration-300 flex flex-col h-full group/card" data-index={idx}>
                        <div className="mb-4 p-3 bg-secondary/5 rounded-full w-fit group-hover/card:bg-secondary/10 transition-colors">
                          {service.icon}
                        </div>
                        <h4 className="text-base font-bold text-primary mb-2">
                          {service.title}
                        </h4>
                        <p className="text-sm text-muted-foreground mb-6 flex-grow">
                          {service.desc}
                        </p>
                        <div className="mt-auto pt-4 border-t border-border/50 flex justify-between items-center">
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Tech Spec
                          </span>
                          <Button variant="ghost" size="sm" className="h-8 text-ring hover:text-ring hover:bg-ring/10 gap-1.5 px-2" asChild>
                            <Link to={service.pdfLink} aria-label={`Download ${service.title} PDF`}>
                              <FileText className="size-3.5" />
                              <span className="text-xs">PDF</span>
                              <Download className="size-3 ml-1" />
                            </Link>
                          </Button>
                        </div>
                      </div>)}
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div className="mt-12 text-center">
          <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20" asChild>
            <Link to="/SolutionsPage">
              View All Solutions <ChevronRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}